import React from 'react';
import { Share2, FolderGit2, GitBranch, CloudUpload, Download, Edit, Trash2 } from 'lucide-react';
import { setActiveTab } from '../../store/uiModalStore';
import { requireCloudSync } from '../../plans/localGate';
import { hasCloudSync } from '../../plans/cloudAccess';
import { isBackendWorkspace } from '../../utils/workspaceStorage';
import { isTeamWorkspace } from '../../utils/workspaceShareEligibility';
import { useGitSyncStore } from '../../store/gitSyncStore';

/** The Git page, showing this workspace (git sync card 8). */
export function openGitPage(workspace) {
  useGitSyncStore.getState().setFocus(workspace.id);
  setActiveTab('git');
}

/**
 * One workspace's "⋯" menu, as ContextMenu items. Shared by the workspace
 * dropdown (WorkspaceManager) and the project switcher's API Client ›
 * Workspaces submenu (21.10.2026). `ContextMenu` filters falsy entries, so
 * each action stays a plain conditional.
 *
 * `c` carries the handlers and state: onShareWorkspace, onForkWorkspace,
 * onPublishLocalWorkspace, onEditWorkspace, onDeleteWorkspace,
 * isAuthenticated, forkingWorkspaceId, publishingWorkspaceId; `t` is i18next.
 */
export function workspaceMenuItems(workspace, c, t) {
  return [
    c.onShareWorkspace &&
      c.isAuthenticated &&
      workspace.storage !== 'local' && {
        icon: <Share2 size={14} />,
        label: 'Share workspace',
        onClick: () => c.onShareWorkspace(workspace),
      },
    // Git lives on the Git page (git sync card 8); the menu only points there.
    workspace.storage !== 'git' && {
      icon: <FolderGit2 size={14} />,
      label: 'Open Git page',
      onClick: () => openGitPage(workspace),
    },
    c.onForkWorkspace && {
      icon: <GitBranch size={14} />,
      label: 'Fork workspace',
      disabled: c.forkingWorkspaceId === workspace.id,
      onClick: () => c.onForkWorkspace(workspace),
    },
    c.isAuthenticated &&
      c.onPublishLocalWorkspace &&
      workspace.storage === 'local' && {
        icon: <CloudUpload size={14} />,
        label: 'Publish to cloud',
        disabled: c.publishingWorkspaceId === workspace.id,
        // Plans v2 card 3.4: without cloud sync this opens the upgrade.
        onClick: () => requireCloudSync() && c.onPublishLocalWorkspace(workspace),
      },
    // A cloud workspace of a plan without cloud sync is read-only: copy it
    // into a workspace on this device (the fork dialog, local destination).
    // The cloud copy stays.
    c.isAuthenticated &&
      c.onForkWorkspace &&
      isBackendWorkspace(workspace, c.isAuthenticated) &&
      workspace.storage !== 'git' &&
      !isTeamWorkspace(workspace) &&
      (workspace.role == null || workspace.role === 'owner') &&
      !hasCloudSync() && {
        icon: <Download size={14} />,
        label: t('cloudLocal.downloadToDevice'),
        disabled: c.forkingWorkspaceId === workspace.id,
        onClick: () => c.onForkWorkspace(workspace),
      },
    c.onEditWorkspace && {
      icon: <Edit size={14} />,
      label: 'Edit workspace',
      onClick: () => c.onEditWorkspace(workspace),
    },
    c.onDeleteWorkspace && {
      icon: <Trash2 size={14} />,
      label: 'Delete workspace',
      onClick: () => c.onDeleteWorkspace(workspace.id),
    },
  ];
}
